"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

// Extend Window with gtag function
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Sends a page_view event to GA4 via gtag() on every
 * client-side route change so GA4 can track SPA navigations.
 *
 * The first pageview is handled by the gtag('config', ...) call
 * on initial page load, so we skip the very first render.
 */
export default function RouteChangeTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the initial page load — gtag config handles that one
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Build full URL path including query string
    const url = searchParams.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;

    // Send page_view event directly to GA4 via gtag
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: url,
        page_location: window.location.origin + url,
      });
    }
  }, [pathname, searchParams]);

  return null;
}
